<template>
  <div class="breadcrumb-nav-wrapper mb-3">
    <!-- Breadcrumb trail (when we have trail data) -->
    <nav v-if="trail.length" class="d-flex align-items-center flex-wrap gap-1">
      <button class="btn btn-sm btn-outline-secondary rounded-pill px-3 py-1 me-2 go-back-btn"
        @click="goBack">
        <i class="material-icons text-sm me-1" style="vertical-align: middle;">arrow_back</i>
        Back
      </button>

      <ol class="breadcrumb mb-0 d-flex align-items-center flex-wrap">
        <li v-for="(crumb, i) in trail" :key="i"
          class="breadcrumb-item"
          :class="{ active: i === trail.length - 1 }">
          <a v-if="i < trail.length - 1 && crumb.route"
            href="#"
            class="crumb-link"
            @click.prevent="navigateTo(i)">
            <i v-if="crumb.icon" class="material-icons crumb-icon">{{ crumb.icon }}</i>
            {{ crumb.label }}
          </a>
          <span v-else class="crumb-current">
            <i v-if="crumb.icon" class="material-icons crumb-icon">{{ crumb.icon }}</i>
            {{ crumb.label }}
          </span>
        </li>
      </ol>

      <span v-if="trail.length > 1" class="depth-badge ms-2">
        Level {{ trail.length - 1 }}
      </span>
    </nav>

    <!-- Fallback: simple Go Back button when no trail (direct URL access) -->
    <button v-else class="btn btn-sm btn-outline-secondary rounded-pill px-3 py-1 go-back-btn"
      @click="goBack">
      <i class="material-icons text-sm me-1" style="vertical-align: middle;">arrow_back</i>
      Go Back
    </button>
  </div>
</template>

<script setup>
import { useRouter } from "vue-router";
import { useBreadcrumb } from "@/composables/useBreadcrumb";

const props = defineProps({
  /** Fallback route when there's no browser history */
  fallbackRoute: { type: String, default: "/" },
});

const router = useRouter();
const { trail, navigateTo } = useBreadcrumb();

function goBack() {
  if (trail.value.length > 1) {
    // Navigate to the parent crumb (second to last)
    navigateTo(trail.value.length - 2);
  } else if (window.history.length > 1) {
    router.go(-1);
  } else {
    router.push(props.fallbackRoute);
  }
}
</script>

<style scoped>
.breadcrumb-nav-wrapper {
  min-height: 36px;
}

.go-back-btn {
  font-size: 0.78rem;
  font-weight: 600;
  color: #6c757d;
  border-color: #dee2e6;
  background: #f8f9fa;
  transition: all 0.2s;
}
.go-back-btn:hover {
  background: #e9ecef;
  color: #495057;
  border-color: #ced4da;
}

/* Breadcrumb trail */
.breadcrumb {
  background: none;
  padding: 0;
  list-style: none;
  font-size: 0.82rem;
}
.breadcrumb-item + .breadcrumb-item::before {
  content: "";
  display: inline-block;
  width: 0;
  height: 0;
  border-top: 5px solid transparent;
  border-bottom: 5px solid transparent;
  border-left: 6px solid #adb5bd;
  margin: 0 0.45rem;
  vertical-align: middle;
}

.crumb-link {
  color: #667eea;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.crumb-link:hover {
  color: #4c63d2;
  text-decoration: underline;
}

.crumb-current {
  color: #344767;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 3px;
}

.crumb-icon {
  font-size: 16px;
  vertical-align: middle;
}

.depth-badge {
  font-size: 0.65rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  background: #e8eaf6;
  color: #667eea;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  white-space: nowrap;
}
</style>
